import { AfterViewInit, Component, ElementRef, inject, OnChanges, ViewChild, ChangeDetectionStrategy, input } from '@angular/core';
import { ConfigurationSection, UserStateService } from '../../../services/user-state.service';
import { Chart, registerables } from 'chart.js';
import { LineChartDataset } from '../../../model/chart-data/line-chart-dataset';
import { FanCurve } from '../../../model/ratings/fan-curve';
import { AhyOperatingPoint } from '../../../model/ahy-operating-point';

@Component({
  selector: 'app-air-handler-unit-fan-curve-chart',
  imports: [],
  templateUrl: './air-handler-unit-fan-curve-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './air-handler-unit-fan-curve-chart.component.css',
})
export class AirHandlerUnitFanCurveChartComponent implements OnChanges, AfterViewInit {
  private userStateService = inject(UserStateService);
  private sectionKey = ConfigurationSection.FAN_CURVE_CHART;
  @ViewChild('lineChart') private lineChartReference: ElementRef | undefined;
  readonly fanCurves = input<FanCurve[]>([]);
  readonly maxAirflow = input(0);
  readonly maxTsp = input(0);
  readonly operatingPoint = input.required<AhyOperatingPoint>();
  isExpanded = true;
  private chart: Chart | undefined;

  constructor() {
    Chart.register(...registerables);
  }

  ngAfterViewInit(): void {
    this.buildChart();
  }

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.data.datasets = this.buildDatasets();
      this.chart.options.scales!['x']!.max = this.maxAirflow();
      this.chart.update();
    }
  }

  toggleExpansion(): void {
    this.isExpanded = !this.isExpanded;
    this.userStateService.setCollapsedValue(this.sectionKey, this.isExpanded);
  }

  getExpansionIcon(): string {
    return this.isExpanded ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  private buildChart(): void {
    this.chart = new Chart(this.lineChartReference?.nativeElement, {
      type: 'line',
      data: {
        datasets: this.buildDatasets(),
      },
      options: {
        layout: {
          padding: {
            right: 60,
          },
        },
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
          },
          tooltip: {
            callbacks: {
              title: function (context) {
                const valueLabel = context[0].dataset.label;
                return `${valueLabel}`;
              },
              label: function (context) {
                const label = context.label || '';
                const value = context.formattedValue;
                return `SCFM: ${label} - TSP: ${value}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'TSP (inWc)',
            },
            ticks: {
              stepSize: 2,
            },
            max: this.maxTsp(),
          },
          x: {
            type: 'linear',
            beginAtZero: true,
            title: {
              display: true,
              text: 'SCFM',
            },
            ticks: {
              stepSize: 1000,
            },
            max: this.maxAirflow(),
          },
        },
      },
    });
  }

  private buildDatasets(): LineChartDataset[] {
    const datasets: LineChartDataset[] = [];
    if ((this.fanCurves()?.length || 0) > 0) {
      for (const fanCurve of this.fanCurves()) {
        const dataset: LineChartDataset = this.initializeDataset(fanCurve.Label);
        for (const point of fanCurve.Points) {
          const pointData = { x: point.Airflow, y: point.TotalStaticPressure };
          dataset.data.push(pointData);
        }
        datasets.push(dataset);
      }
    }
    if (this.operatingPoint()) {
      const dataset: LineChartDataset = this.initializeOperatingPointDataset();
      const pointData = { x: this.operatingPoint().airflow, y: this.operatingPoint().staticPressure };
      dataset.data.push(pointData);
      datasets.push(dataset);
    }
    return datasets;
  }

  private initializeOperatingPointDataset(): LineChartDataset {
    const dataset: LineChartDataset = new LineChartDataset();
    const color = 'rgb(78, 3, 122)';
    dataset.backgroundColor = color;
    dataset.borderColor = color;
    dataset.label = 'Operating Point';
    dataset.fill = true;
    dataset.pointRadius = 4;
    dataset.data = [];
    return dataset;
  }

  private initializeDataset(label: string): LineChartDataset {
    const dataset: LineChartDataset = new LineChartDataset();
    const color = this.getLineColor(label);
    dataset.backgroundColor = color;
    dataset.borderColor = color;
    if (label === 'Operating Curve' || label === 'System Curve') {
      dataset.borderDash = [9, 10];
    }
    dataset.label = label;
    dataset.fill = false;
    dataset.pointRadius = 0;
    dataset.data = [];
    return dataset;
  }

  private getLineColor(label: string): string {
    switch (label) {
      case 'Surge/Limit':
        return 'rgb(255, 0, 0)';
      case 'System Curve':
        return 'rgb(227, 119, 194)';
      case 'Operating Curve':
        return 'rgb(78, 3, 122)';
      default:
        return this.getRandomColor();
    }
  }

  private getRandomColor(): string {
    const r = Math.floor(Math.random() * 255);
    const g = Math.floor(Math.random() * 255);
    const b = Math.floor(Math.random() * 255);
    return `rgb(${r}, ${g}, ${b})`;
  }
}
